import { useState } from 'react';
import { MagnifyingGlassIcon, PlusIcon } from '@heroicons/react/24/outline';
import { Input, Spinner } from '../../../components';
import { useConversationStore } from '../../../stores';
import { useConversations } from '../hooks/useConversations';
import ConversationItem from './ConversationItem';
import ConversationFilter from './ConversationFilter';
import NewConversationModal from './NewConversationModal';

export default function ConversationList() {
  const { searchQuery, setSearchQuery, getFilteredConversations } = useConversationStore();
  const { isLoading, hasMore, loadMore, loadingMore } = useConversations();
  const [isComposeOpen, setIsComposeOpen] = useState(false);

  const filteredConversations = getFilteredConversations();

  return (
    <div className="flex h-full flex-col border-r border-line bg-surface">
      {/* Header */}
      <div className="border-b border-line p-4">
        <div className="flex items-center justify-between">
          <h2 className="font-display text-lg font-semibold text-ink">Conversations</h2>
          <button
            onClick={() => setIsComposeOpen(true)}
            className="inline-flex items-center gap-1.5 rounded-pill bg-brand px-2.5 py-1.5 text-xs font-medium text-white transition-colors hover:bg-brand/90"
            title="Start a new WhatsApp conversation"
          >
            <PlusIcon className="h-4 w-4" />
            New
          </button>
        </div>

        {/* Search */}
        <div className="mt-3">
          <Input
            type="text"
            placeholder="Search conversations..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            leftIcon={<MagnifyingGlassIcon className="h-5 w-5" />}
          />
        </div>

        {/* Filter */}
        <div className="mt-3">
          <ConversationFilter />
        </div>
      </div>

      {/* Conversation List */}
      <div className="flex-1 overflow-y-auto scrollbar-thin">
        {isLoading ? (
          <div className="flex h-full items-center justify-center">
            <Spinner />
          </div>
        ) : filteredConversations.length === 0 ? (
          <div className="flex h-full items-center justify-center p-4 text-center">
            <p className="text-sm text-ink-3">No conversations found</p>
          </div>
        ) : (
          <div className="divide-y divide-line">
            {filteredConversations.map((conversation, idx) => {
              // Cluster sequential rows for the same customer so multiple
              // active/historical threads read as one customer's stack.
              const prev = idx > 0 ? filteredConversations[idx - 1] : undefined;
              const isContinuation =
                !!prev &&
                ((!!conversation.customerPhone &&
                  prev.customerPhone === conversation.customerPhone) ||
                  (!!conversation.customerId &&
                    prev.customerId === conversation.customerId));
              return (
                <ConversationItem
                  key={conversation.id}
                  conversation={conversation}
                  isContinuation={isContinuation}
                />
              );
            })}
            {hasMore && (
              <div className="flex justify-center p-3">
                <button
                  onClick={loadMore}
                  disabled={loadingMore}
                  className="rounded-full border border-line bg-surface px-4 py-1.5 text-xs font-medium text-ink-2 hover:bg-black/[0.04] disabled:opacity-50"
                >
                  {loadingMore ? 'Loading...' : 'Load more'}
                </button>
              </div>
            )}
          </div>
        )}
      </div>

      <NewConversationModal
        isOpen={isComposeOpen}
        onClose={() => setIsComposeOpen(false)}
      />
    </div>
  );
}
